import type { ReactNode } from "react";
import type { ValidationProblem, ValidationReport } from "../lib/survey-validation.js";

/**
 * The problem count on the drawing, and the list behind it.
 *
 * A heat loss is a sum, and a guess adds up the same way a measurement does —
 * so a survey nobody has touched still produces a confident-looking total.
 * This is the count that turns "is this finished?" from a judgement into a
 * number, and the list that turns the number back into work: each row names
 * the room or the object it is about and selects it on the plan.
 *
 * BLOCKING AND WARNING LOOK DIFFERENT, ON PURPOSE. The count on the badge is
 * the blocking one, because those are the problems that make the figure wrong
 * rather than merely unreviewed. Warnings are listed under their own heading
 * and are never added to that number: a count that includes things which do
 * not change the answer is a count people learn to ignore.
 */

export function ValidationBadge({
  report,
}: {
  readonly report: ValidationReport;
}): ReactNode {
  if (report.blocking === 0 && report.warnings === 0) {
    return (
      <span className="validation-badge validation-badge--clear" data-testid="validation-badge">
        No problems
      </span>
    );
  }
  return (
    <span className="validation-badge" data-testid="validation-badge">
      {report.blocking > 0 ? (
        <span
          className="validation-badge__count validation-badge__count--blocking"
          data-testid="validation-blocking-count"
        >
          {report.blocking} {report.blocking === 1 ? "problem" : "problems"}
        </span>
      ) : null}
      {report.warnings > 0 ? (
        <span
          className="validation-badge__count validation-badge__count--warning"
          data-testid="validation-warning-count"
        >
          {report.warnings} {report.warnings === 1 ? "warning" : "warnings"}
        </span>
      ) : null}
    </span>
  );
}

export function ValidationList({
  report,
  onGoTo,
}: {
  readonly report: ValidationReport;
  /**
   * Take me to it. Given the scan element to select on the plan (a room's or a
   * wall's external ref, null when there is nothing to select) and the problem
   * itself, so a caller can also open the right panel.
   */
  readonly onGoTo: (problem: ValidationProblem) => void;
}): ReactNode {
  const blocking = report.problems.filter((p) => p.severity === "blocking");
  const warnings = report.problems.filter((p) => p.severity === "warning");

  return (
    <section className="validation" data-testid="validation-list" aria-label="Checks">
      <header className="validation__header">
        <h3 className="validation__title">Checks</h3>
        <ValidationBadge report={report} />
      </header>

      {report.problems.length === 0 ? (
        <p className="muted small" data-testid="validation-clear">
          Nothing is blocking a correct heat loss. Every room has a type and a
          temperature, every surface has a U-value somebody chose, and every wall
          and floor has an answer for what is on the other side.
        </p>
      ) : null}

      {blocking.length > 0 ? (
        <>
          <h4 className="validation__heading validation__heading--blocking">
            Blocking a correct heat loss
          </h4>
          <ul className="validation__group" data-testid="validation-blocking">
            {blocking.map((problem) => (
              <ProblemRow key={rowKey(problem)} problem={problem} onGoTo={onGoTo} />
            ))}
          </ul>
        </>
      ) : null}

      {warnings.length > 0 ? (
        <>
          <h4 className="validation__heading validation__heading--warning">
            Worth a look — these do not change the heat loss
          </h4>
          <ul className="validation__group" data-testid="validation-warnings">
            {warnings.map((problem) => (
              <ProblemRow key={rowKey(problem)} problem={problem} onGoTo={onGoTo} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function rowKey(problem: ValidationProblem): string {
  return `${problem.kind}:${problem.surfaceId ?? problem.objectId ?? problem.roomId ?? "survey"}`;
}

function ProblemRow({
  problem,
  onGoTo,
}: {
  readonly problem: ValidationProblem;
  readonly onGoTo: (problem: ValidationProblem) => void;
}): ReactNode {
  return (
    <li
      className={`validation__item validation__item--${problem.severity}`}
      data-testid={`validation-${problem.kind}`}
    >
      {/* A button, not a #fragment link: the portal's router owns the hash. */}
      <button type="button" className="link validation__go" onClick={() => onGoTo(problem)}>
        {problem.roomName ?? "This survey"}
      </button>
      <span className="validation__message">{problem.message}</span>
    </li>
  );
}
